import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';
import { palette } from '../../theme';
import { builtInAssets, findBuiltIn, type BuiltInAsset } from './builtInAssets';
import { scenePresets, findPreset, ViewportGrid } from './presets';
import {
  loadFromArrayBuffer,
  loadFromUrl,
  type LoadedGltfAsset,
} from './gltfLoader';

// ---------------------------------------------------------------------------
// Asset viewer. Three-panel layout:
//   - left:   asset list (built-ins + loaded GLTF, plus URL input)
//   - center: R3F viewport rendering the active preset + active asset
//   - right:  inspector with metadata + animation controls
//
// State is plain React. Per the spec, tools intentionally do not reach for
// Koota for state that lives entirely inside one tool.
// ---------------------------------------------------------------------------

type ActiveAsset =
  | { kind: 'builtin'; asset: BuiltInAsset }
  | { kind: 'gltf'; asset: LoadedGltfAsset };

export interface AssetTransform {
  pos: [number, number, number];
  rotDeg: [number, number, number];
  scale: [number, number, number];
}

const IDENTITY_TRANSFORM: AssetTransform = {
  pos: [0, 0, 0],
  rotDeg: [0, 0, 0],
  scale: [1, 1, 1],
};

const FALLBACK_ASSET_ID = builtInAssets[0]?.id ?? '';

export function AssetViewer() {
  const [selectedAssetId, setSelectedAssetId] = useState<string>(FALLBACK_ASSET_ID);
  const [selectedPresetId, setSelectedPresetId] = useState<string>(
    scenePresets[0]?.id ?? 'stage'
  );
  const [showGrid, setShowGrid] = useState(true);
  const [loaded, setLoaded] = useState<LoadedGltfAsset[]>([]);
  const [urlInput, setUrlInput] = useState('');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [animClip, setAnimClip] = useState<string | null>(null);
  const [animPlaying, setAnimPlaying] = useState(false);
  const [frameRequest, setFrameRequest] = useState(0);
  // Per-asset transform: viewing a different asset shouldn't carry over the
  // previous asset's rotation. Keyed by asset id; default identity.
  const [transformsById, setTransformsById] = useState<Record<string, AssetTransform>>(
    {}
  );
  const transform = transformsById[selectedAssetId] ?? IDENTITY_TRANSFORM;
  const updateTransform = useCallback(
    (next: AssetTransform) => {
      setTransformsById(prev => ({ ...prev, [selectedAssetId]: next }));
    },
    [selectedAssetId]
  );
  const resetTransform = useCallback(() => {
    setTransformsById(prev => {
      if (!(selectedAssetId in prev)) return prev;
      const next = { ...prev };
      delete next[selectedAssetId];
      return next;
    });
  }, [selectedAssetId]);

  const active: ActiveAsset | null = useMemo(() => {
    const builtin = findBuiltIn(selectedAssetId);
    if (builtin) return { kind: 'builtin', asset: builtin };
    const gltf = loaded.find(l => l.id === selectedAssetId);
    if (gltf) return { kind: 'gltf', asset: gltf };
    return null;
  }, [selectedAssetId, loaded]);

  const preset = useMemo(
    () => findPreset(selectedPresetId) ?? scenePresets[0],
    [selectedPresetId]
  );

  // When the active asset changes to a different GLTF, reset its animation
  // controls. Built-ins have no animations.
  useEffect(() => {
    if (active?.kind === 'gltf') {
      const clips = active.asset.gltf.animations;
      setAnimClip(clips.length > 0 ? clips[0].name : null);
      setAnimPlaying(false);
    } else {
      setAnimClip(null);
      setAnimPlaying(false);
    }
  }, [active]);

  // Drag-drop loading. The handlers attach to the entire viewer container so
  // dropping anywhere over the viewer counts.
  const onDrop = useCallback(async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setLoadError(null);
    const files = Array.from(e.dataTransfer.files).filter(f =>
      f.name.toLowerCase().endsWith('.glb') || f.name.toLowerCase().endsWith('.gltf')
    );
    if (files.length === 0) {
      setLoadError('Drop a .glb or .gltf file.');
      return;
    }
    setIsLoading(true);
    try {
      for (const file of files) {
        const buf = await file.arrayBuffer();
        const asset = await loadFromArrayBuffer(buf, file.name);
        setLoaded(prev => [...prev, asset]);
        setSelectedAssetId(asset.id);
      }
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : String(err));
    } finally {
      setIsLoading(false);
    }
  }, []);

  const onDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  }, []);

  const onLoadUrl = useCallback(async () => {
    if (!urlInput.trim()) return;
    setLoadError(null);
    setIsLoading(true);
    try {
      const asset = await loadFromUrl(urlInput.trim());
      setLoaded(prev => [...prev, asset]);
      setSelectedAssetId(asset.id);
      setUrlInput('');
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : String(err));
    } finally {
      setIsLoading(false);
    }
  }, [urlInput]);

  const onUnload = useCallback(
    (id: string) => {
      setLoaded(prev => prev.filter(a => a.id !== id));
      setTransformsById(prev => {
        if (!(id in prev)) return prev;
        const next = { ...prev };
        delete next[id];
        return next;
      });
      // If we just unloaded the active asset, fall back to the first
      // built-in so the viewport doesn't go blank.
      setSelectedAssetId(curr => (curr === id ? FALLBACK_ASSET_ID : curr));
    },
    []
  );

  return (
    <div
      onDrop={onDrop}
      onDragOver={onDragOver}
      style={{
        display: 'grid',
        gridTemplateColumns: '260px 1fr 280px',
        height: '100%',
        gap: 0,
        background: palette.barkBrown,
      }}
    >
      <Sidebar
        loaded={loaded}
        selectedId={selectedAssetId}
        onSelect={setSelectedAssetId}
        onUnload={onUnload}
        urlInput={urlInput}
        setUrlInput={setUrlInput}
        onLoadUrl={onLoadUrl}
        isLoading={isLoading}
        loadError={loadError}
      />
      <Viewport
        active={active}
        preset={preset}
        showGrid={showGrid}
        transform={transform}
        animClip={animClip}
        animPlaying={animPlaying}
        frameRequest={frameRequest}
      />
      <Inspector
        active={active}
        preset={preset}
        onPresetChange={setSelectedPresetId}
        showGrid={showGrid}
        setShowGrid={setShowGrid}
        transform={transform}
        updateTransform={updateTransform}
        resetTransform={resetTransform}
        animClip={animClip}
        setAnimClip={setAnimClip}
        animPlaying={animPlaying}
        setAnimPlaying={setAnimPlaying}
        onFrame={() => setFrameRequest(n => n + 1)}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sidebar — asset list + URL input + drag-drop hint
// ---------------------------------------------------------------------------

function Sidebar({
  loaded,
  selectedId,
  onSelect,
  onUnload,
  urlInput,
  setUrlInput,
  onLoadUrl,
  isLoading,
  loadError,
}: {
  loaded: LoadedGltfAsset[];
  selectedId: string;
  onSelect: (id: string) => void;
  onUnload: (id: string) => void;
  urlInput: string;
  setUrlInput: (v: string) => void;
  onLoadUrl: () => void;
  isLoading: boolean;
  loadError: string | null;
}) {
  return (
    <aside
      style={{
        background: 'rgba(0,0,0,0.25)',
        borderRight: `1px solid ${palette.mossTeal}`,
        padding: 12,
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
      }}
    >
      <Section title="Built-in">
        {builtInAssets.map(a => (
          <AssetRow
            key={a.id}
            id={a.id}
            name={a.name}
            description={a.description}
            selected={a.id === selectedId}
            onSelect={onSelect}
          />
        ))}
      </Section>
      <Section title="Loaded">
        {loaded.length === 0 && (
          <div style={{ opacity: 0.55, fontSize: 12 }}>
            No assets loaded. Drop a .glb / .gltf file anywhere on the
            viewer, or paste a URL below.
          </div>
        )}
        {loaded.map(a => (
          <AssetRow
            key={a.id}
            id={a.id}
            name={a.name}
            description={`from ${a.source}`}
            selected={a.id === selectedId}
            onSelect={onSelect}
            onUnload={onUnload}
          />
        ))}
      </Section>
      <Section title="Load from URL">
        <input
          type="text"
          placeholder="https://example.com/model.glb"
          value={urlInput}
          onChange={e => setUrlInput(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') onLoadUrl();
          }}
          style={{
            background: 'rgba(0,0,0,0.4)',
            border: `1px solid ${palette.mossTeal}`,
            color: palette.warmBeige,
            padding: '6px 8px',
            borderRadius: 4,
            fontSize: 12,
            width: '100%',
            boxSizing: 'border-box',
          }}
        />
        <button
          type="button"
          onClick={onLoadUrl}
          disabled={!urlInput.trim() || isLoading}
          style={{
            ...btnStyle,
            marginTop: 6,
            opacity: !urlInput.trim() || isLoading ? 0.5 : 1,
          }}
        >
          {isLoading ? 'Loading…' : 'Load'}
        </button>
        {loadError && (
          <div
            style={{
              marginTop: 6,
              fontSize: 11,
              color: palette.acornOrange,
              wordBreak: 'break-word',
            }}
          >
            {loadError}
          </div>
        )}
      </Section>
    </aside>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div
        style={{
          fontSize: 11,
          letterSpacing: 1.2,
          textTransform: 'uppercase',
          opacity: 0.6,
          marginBottom: 6,
        }}
      >
        {title}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {children}
      </div>
    </div>
  );
}

function AssetRow({
  id,
  name,
  description,
  selected,
  onSelect,
  onUnload,
}: {
  id: string;
  name: string;
  description?: string;
  selected: boolean;
  onSelect: (id: string) => void;
  onUnload?: (id: string) => void;
}) {
  return (
    <div
      style={{
        position: 'relative',
        background: selected ? palette.acornOrange : 'rgba(255,255,255,0.04)',
        color: selected ? palette.barkBrown : palette.warmBeige,
        borderRadius: 4,
      }}
    >
      <button
        type="button"
        onClick={() => onSelect(id)}
        style={{
          textAlign: 'left',
          background: 'transparent',
          color: 'inherit',
          border: 'none',
          padding: '6px 8px',
          paddingRight: onUnload ? 28 : 8,
          borderRadius: 4,
          cursor: 'pointer',
          fontFamily: 'system-ui, sans-serif',
          fontSize: 13,
          width: '100%',
        }}
      >
        <div style={{ fontWeight: selected ? 600 : 400 }}>{name}</div>
        {description && (
          <div
            style={{
              fontSize: 11,
              opacity: selected ? 0.75 : 0.55,
              marginTop: 2,
            }}
          >
            {description}
          </div>
        )}
      </button>
      {onUnload && (
        <button
          type="button"
          aria-label={`Unload ${name}`}
          title="Unload"
          onClick={e => {
            e.stopPropagation();
            onUnload(id);
          }}
          style={{
            position: 'absolute',
            top: 4,
            right: 4,
            background: 'transparent',
            color: 'inherit',
            border: 'none',
            cursor: 'pointer',
            fontSize: 14,
            lineHeight: 1,
            padding: '2px 6px',
            borderRadius: 3,
            opacity: 0.6,
          }}
          onMouseEnter={e => {
            e.currentTarget.style.opacity = '1';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.opacity = '0.6';
          }}
        >
          ×
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Viewport — R3F Canvas with the active preset + active asset.
//
// Camera controls: a hand-rolled orbit controller. Drei has OrbitControls
// but it ships an extension `THREE.EventDispatcher` extension and isn't
// currently used in-game; keeping this self-contained avoids dragging in
// the dependency for one component. (Same as the spec's "raw three where
// more direct" note.)
// ---------------------------------------------------------------------------

function Viewport({
  active,
  preset,
  showGrid,
  transform,
  animClip,
  animPlaying,
  frameRequest,
}: {
  active: ActiveAsset | null;
  preset: ReturnType<typeof findPreset>;
  showGrid: boolean;
  transform: AssetTransform;
  animClip: string | null;
  animPlaying: boolean;
  frameRequest: number;
}) {
  if (!preset) return null;
  return (
    <div style={{ position: 'relative', height: '100%' }}>
      <Canvas
        shadows
        camera={{ position: [3, 2.5, 4], fov: 45 }}
        style={{ background: preset.background }}
      >
        {preset.render()}
        {showGrid && <ViewportGrid />}
        <ActiveAssetRenderer
          active={active}
          transform={transform}
          animClip={animClip}
          animPlaying={animPlaying}
        />
        <OrbitCam frameRequest={frameRequest} target={active} />
      </Canvas>
      <ViewportLegend active={active} preset={preset} />
    </div>
  );
}

function ActiveAssetRenderer({
  active,
  transform,
  animClip,
  animPlaying,
}: {
  active: ActiveAsset | null;
  transform: AssetTransform;
  animClip: string | null;
  animPlaying: boolean;
}) {
  if (!active) return null;
  const rotRad: [number, number, number] = [
    (transform.rotDeg[0] * Math.PI) / 180,
    (transform.rotDeg[1] * Math.PI) / 180,
    (transform.rotDeg[2] * Math.PI) / 180,
  ];
  return (
    <group position={transform.pos} rotation={rotRad} scale={transform.scale}>
      {active.kind === 'builtin' ? (
        active.asset.render()
      ) : (
        <GltfAsset
          asset={active.asset}
          animClip={animClip}
          animPlaying={animPlaying}
        />
      )}
    </group>
  );
}

function GltfAsset({
  asset,
  animClip,
  animPlaying,
}: {
  asset: LoadedGltfAsset;
  animClip: string | null;
  animPlaying: boolean;
}) {
  const mixerRef = useRef<THREE.AnimationMixer | null>(null);
  const actionRef = useRef<THREE.AnimationAction | null>(null);

  // (Re)build mixer when the asset changes.
  useEffect(() => {
    const mixer = new THREE.AnimationMixer(asset.gltf.scene);
    mixerRef.current = mixer;
    return () => {
      mixer.stopAllAction();
      mixerRef.current = null;
      actionRef.current = null;
    };
  }, [asset]);

  // Swap or stop the action when the selected clip changes.
  useEffect(() => {
    const mixer = mixerRef.current;
    if (!mixer) return;
    actionRef.current?.stop();
    actionRef.current = null;
    if (!animClip) return;
    const clip = asset.gltf.animations.find(c => c.name === animClip);
    if (!clip) return;
    const action = mixer.clipAction(clip);
    actionRef.current = action;
    action.reset();
    action.play();
    action.paused = !animPlaying;
  }, [asset, animClip, animPlaying]);

  // Toggle pause on the running action without restarting it.
  useEffect(() => {
    const action = actionRef.current;
    if (!action) return;
    action.paused = !animPlaying;
  }, [animPlaying]);

  useFrame((_, delta) => {
    mixerRef.current?.update(delta);
  });

  return <primitive object={asset.gltf.scene} />;
}

// ---------------------------------------------------------------------------
// Hand-rolled orbit camera. Drag rotates around target, wheel zooms,
// right-drag pans. The "frame asset" button bumps `frameRequest`, which
// snaps the camera to fit the active asset's bounding box.
// ---------------------------------------------------------------------------

function OrbitCam({
  target,
  frameRequest,
}: {
  target: ActiveAsset | null;
  frameRequest: number;
}) {
  const { gl, camera } = useThree();
  const center = useRef(new THREE.Vector3(0, 0.5, 0));
  const spherical = useRef(new THREE.Spherical(6, Math.PI / 3, Math.PI / 4));

  // Frame the active asset on demand. Built-ins use a default framing;
  // GLTF assets use the actual bbox.
  useEffect(() => {
    if (!target) {
      center.current.set(0, 0.5, 0);
      spherical.current.set(6, Math.PI / 3, Math.PI / 4);
      return;
    }
    if (target.kind === 'gltf') {
      const bbox = target.asset.bbox;
      const size = new THREE.Vector3();
      bbox.getSize(size);
      const c = new THREE.Vector3();
      bbox.getCenter(c);
      const maxDim = Math.max(size.x, size.y, size.z) || 1;
      // Heuristic: distance ~ 1.6× max dim, slightly elevated angle.
      const dist = Math.max(2, maxDim * 1.6);
      center.current.copy(c);
      spherical.current.set(dist, Math.PI / 3, Math.PI / 4);
    } else {
      center.current.set(0, 0.5, 0);
      spherical.current.set(4, Math.PI / 3, Math.PI / 4);
    }
  }, [target, frameRequest]);

  // Mouse + wheel handlers.
  useEffect(() => {
    const dom = gl.domElement;
    let dragging: 'rotate' | 'pan' | null = null;
    let lastX = 0;
    let lastY = 0;

    const onDown = (e: MouseEvent) => {
      e.preventDefault();
      dragging = e.button === 2 ? 'pan' : 'rotate';
      lastX = e.clientX;
      lastY = e.clientY;
    };
    const onMove = (e: MouseEvent) => {
      if (!dragging) return;
      const dx = e.clientX - lastX;
      const dy = e.clientY - lastY;
      lastX = e.clientX;
      lastY = e.clientY;
      if (dragging === 'rotate') {
        const s = spherical.current;
        s.theta -= dx * 0.005;
        s.phi -= dy * 0.005;
        s.phi = Math.max(0.05, Math.min(Math.PI - 0.05, s.phi));
      } else {
        // Pan: move center perpendicular to camera look direction.
        const right = new THREE.Vector3();
        const up = new THREE.Vector3();
        camera.matrix.extractBasis(right, up, new THREE.Vector3());
        const scale = spherical.current.radius * 0.0015;
        center.current.addScaledVector(right, -dx * scale);
        center.current.addScaledVector(up, dy * scale);
      }
    };
    const onUp = () => {
      dragging = null;
    };
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const factor = Math.exp(e.deltaY * 0.001);
      spherical.current.radius = Math.max(
        0.5,
        Math.min(80, spherical.current.radius * factor)
      );
    };
    const onCtx = (e: MouseEvent) => {
      e.preventDefault();
    };

    dom.addEventListener('mousedown', onDown);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    dom.addEventListener('wheel', onWheel, { passive: false });
    dom.addEventListener('contextmenu', onCtx);
    return () => {
      dom.removeEventListener('mousedown', onDown);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      dom.removeEventListener('wheel', onWheel);
      dom.removeEventListener('contextmenu', onCtx);
    };
  }, [gl, camera]);

  useFrame(() => {
    const offset = new THREE.Vector3().setFromSpherical(spherical.current);
    camera.position.copy(center.current).add(offset);
    camera.lookAt(center.current);
  });

  return null;
}

function ViewportLegend({
  active,
  preset,
}: {
  active: ActiveAsset | null;
  preset: NonNullable<ReturnType<typeof findPreset>>;
}) {
  return (
    <div
      style={{
        position: 'absolute',
        bottom: 8,
        left: 8,
        background: 'rgba(0,0,0,0.55)',
        color: palette.warmBeige,
        padding: '6px 10px',
        borderRadius: 4,
        fontSize: 11,
        fontFamily: 'system-ui, sans-serif',
        pointerEvents: 'none',
      }}
    >
      <div>{preset.name} · {active ? labelOf(active) : 'no asset'}</div>
      <div style={{ opacity: 0.6, marginTop: 2 }}>
        Drag to orbit · wheel to zoom · right-drag to pan
      </div>
    </div>
  );
}

function labelOf(active: ActiveAsset): string {
  return active.kind === 'builtin' ? active.asset.name : active.asset.name;
}

// ---------------------------------------------------------------------------
// Inspector — preset switcher, metadata, animation controls
// ---------------------------------------------------------------------------

function Inspector({
  active,
  preset,
  onPresetChange,
  showGrid,
  setShowGrid,
  transform,
  updateTransform,
  resetTransform,
  animClip,
  setAnimClip,
  animPlaying,
  setAnimPlaying,
  onFrame,
}: {
  active: ActiveAsset | null;
  preset: NonNullable<ReturnType<typeof findPreset>>;
  onPresetChange: (id: string) => void;
  showGrid: boolean;
  setShowGrid: (v: boolean) => void;
  transform: AssetTransform;
  updateTransform: (next: AssetTransform) => void;
  resetTransform: () => void;
  animClip: string | null;
  setAnimClip: (id: string | null) => void;
  animPlaying: boolean;
  setAnimPlaying: (v: boolean) => void;
  onFrame: () => void;
}) {
  return (
    <aside
      style={{
        background: 'rgba(0,0,0,0.25)',
        borderLeft: `1px solid ${palette.mossTeal}`,
        padding: 12,
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
      }}
    >
      <Section title="Stage">
        <select
          value={preset.id}
          onChange={e => onPresetChange(e.target.value)}
          style={selectStyle}
        >
          {scenePresets.map(p => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
        <div style={{ fontSize: 11, opacity: 0.6, marginTop: 4 }}>
          {preset.description}
        </div>
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            marginTop: 8,
            fontSize: 12,
            cursor: 'pointer',
          }}
        >
          <input
            type="checkbox"
            checked={showGrid}
            onChange={e => setShowGrid(e.target.checked)}
          />
          Show grid (1m squares)
        </label>
      </Section>

      <Section title="Camera">
        <button type="button" onClick={onFrame} style={btnStyle}>
          Frame asset
        </button>
      </Section>

      <Section title="Transform">
        <TransformInputs
          transform={transform}
          onChange={updateTransform}
        />
        <button
          type="button"
          onClick={resetTransform}
          style={{ ...btnStyle, marginTop: 6 }}
        >
          Reset transform
        </button>
        <div style={{ fontSize: 10, opacity: 0.5, marginTop: 4 }}>
          Per-asset; switching assets keeps each one's transform.
        </div>
      </Section>

      {active?.kind === 'gltf' && active.asset.gltf.animations.length > 0 ? (
        <Section title="Animation">
          <select
            value={animClip ?? ''}
            onChange={e => setAnimClip(e.target.value || null)}
            style={selectStyle}
          >
            {active.asset.gltf.animations.map(c => (
              <option key={c.name} value={c.name}>
                {c.name}
              </option>
            ))}
          </select>
          <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
            <button
              type="button"
              onClick={() => setAnimPlaying(true)}
              disabled={!animClip || animPlaying}
              style={{ ...btnStyle, flex: 1, opacity: !animClip || animPlaying ? 0.5 : 1 }}
            >
              ▶ Play
            </button>
            <button
              type="button"
              onClick={() => setAnimPlaying(false)}
              disabled={!animPlaying}
              style={{ ...btnStyle, flex: 1, opacity: !animPlaying ? 0.5 : 1 }}
            >
              ⏸ Pause
            </button>
          </div>
        </Section>
      ) : active?.kind === 'gltf' ? (
        <Section title="Animation">
          <div style={{ fontSize: 12, opacity: 0.55 }}>
            No animation clips in this asset.
          </div>
        </Section>
      ) : null}

      <Section title="Metadata">
        <Metadata active={active} />
      </Section>
    </aside>
  );
}

function Metadata({ active }: { active: ActiveAsset | null }) {
  if (!active) {
    return <div style={{ fontSize: 12, opacity: 0.55 }}>No asset selected.</div>;
  }
  if (active.kind === 'builtin') {
    return (
      <dl style={dlStyle}>
        <dt>Source</dt>
        <dd>built-in</dd>
        <dt>Id</dt>
        <dd>{active.asset.id}</dd>
        {active.asset.description && (
          <>
            <dt>Description</dt>
            <dd>{active.asset.description}</dd>
          </>
        )}
      </dl>
    );
  }
  const a = active.asset;
  let meshCount = 0;
  let triCount = 0;
  a.gltf.scene.traverse(o => {
    const mesh = o as THREE.Mesh;
    if ((mesh as { isMesh?: boolean }).isMesh && mesh.geometry) {
      meshCount += 1;
      const idx = mesh.geometry.getIndex();
      const pos = mesh.geometry.getAttribute('position');
      if (idx) triCount += idx.count / 3;
      else if (pos) triCount += pos.count / 3;
    }
  });
  const size = new THREE.Vector3();
  a.bbox.getSize(size);
  return (
    <dl style={dlStyle}>
      <dt>Source</dt>
      <dd>{a.source}</dd>
      <dt>Name</dt>
      <dd>{a.name}</dd>
      <dt>Meshes</dt>
      <dd>{meshCount}</dd>
      <dt>Triangles</dt>
      <dd>{Math.round(triCount).toLocaleString()}</dd>
      <dt>Bounds (m)</dt>
      <dd>
        {size.x.toFixed(2)} × {size.y.toFixed(2)} × {size.z.toFixed(2)}
      </dd>
      <dt>Clips</dt>
      <dd>
        {a.gltf.animations.length === 0
          ? 'none'
          : a.gltf.animations.map(c => c.name).join(', ')}
      </dd>
    </dl>
  );
}

// ---------------------------------------------------------------------------
// Transform inputs — three rows (position, rotation in degrees, scale).
// Three number inputs per row. Useful for finding the offset/orientation/
// scale that lands an imported asset in neutral pose; the resulting numbers
// can then be baked into the GLTF or applied at load time in-game.
// ---------------------------------------------------------------------------

function TransformInputs({
  transform,
  onChange,
}: {
  transform: AssetTransform;
  onChange: (next: AssetTransform) => void;
}) {
  const setUniformScale = (s: number) => {
    onChange({ ...transform, scale: [s, s, s] });
  };
  // The slider reflects the X axis. When per-axis values diverge, the
  // slider just shows X — moving it re-uniforms all three. Cheap and
  // unambiguous; the per-axis row covers the non-uniform case.
  const uniform = transform.scale[0];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <Vec3Row
        label="Pos"
        value={transform.pos}
        step={0.1}
        onChange={pos => onChange({ ...transform, pos })}
      />
      <Vec3Row
        label="Rot°"
        value={transform.rotDeg}
        step={5}
        onChange={rotDeg => onChange({ ...transform, rotDeg })}
      />
      <Vec3Row
        label="Scale"
        value={transform.scale}
        step={0.1}
        onChange={scale => onChange({ ...transform, scale })}
      />
      <UniformScaleSlider value={uniform} onChange={setUniformScale} />
    </div>
  );
}

function UniformScaleSlider({
  value,
  onChange,
}: {
  value: number;
  onChange: (s: number) => void;
}) {
  // Empty first cell preserves alignment with the Vec3Row above (which has
  // a 40px label column). The slider itself is self-explanatory sitting
  // under the per-axis Scale row.
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '40px 1fr 48px',
        gap: 4,
        alignItems: 'center',
      }}
    >
      <span />
      <input
        type="range"
        min={0.5}
        max={10}
        step={0.05}
        value={Math.max(0.5, Math.min(10, value))}
        onChange={e => onChange(Number(e.target.value))}
        style={{ width: '100%', accentColor: palette.acornOrange }}
      />
      <span
        style={{
          fontSize: 11,
          opacity: 0.7,
          textAlign: 'right',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value.toFixed(2)}×
      </span>
    </div>
  );
}

function Vec3Row({
  label,
  value,
  step,
  onChange,
}: {
  label: string;
  value: [number, number, number];
  step: number;
  onChange: (next: [number, number, number]) => void;
}) {
  const update = (idx: 0 | 1 | 2, raw: string) => {
    const n = Number(raw);
    if (!Number.isFinite(n)) return;
    const next: [number, number, number] = [...value];
    next[idx] = n;
    onChange(next);
  };
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '40px 1fr 1fr 1fr', gap: 4, alignItems: 'center' }}>
      <span style={{ fontSize: 11, opacity: 0.7 }}>{label}</span>
      {[0, 1, 2].map(i => (
        <input
          key={i}
          type="number"
          step={step}
          value={value[i as 0 | 1 | 2]}
          onChange={e => update(i as 0 | 1 | 2, e.target.value)}
          style={numInputStyle}
        />
      ))}
    </div>
  );
}

const numInputStyle: React.CSSProperties = {
  background: 'rgba(0,0,0,0.4)',
  color: palette.warmBeige,
  border: `1px solid ${palette.mossTeal}`,
  padding: '3px 4px',
  borderRadius: 3,
  fontSize: 11,
  fontFamily: 'system-ui, sans-serif',
  width: '100%',
  boxSizing: 'border-box',
};

const dlStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'auto 1fr',
  gap: '4px 12px',
  margin: 0,
  fontSize: 12,
};

const btnStyle: React.CSSProperties = {
  background: palette.mossTeal,
  color: palette.warmBeige,
  border: 'none',
  padding: '6px 10px',
  borderRadius: 4,
  cursor: 'pointer',
  fontSize: 12,
  fontFamily: 'system-ui, sans-serif',
};

const selectStyle: React.CSSProperties = {
  background: 'rgba(0,0,0,0.4)',
  color: palette.warmBeige,
  border: `1px solid ${palette.mossTeal}`,
  padding: '4px 8px',
  borderRadius: 4,
  fontSize: 12,
  fontFamily: 'system-ui, sans-serif',
  width: '100%',
  boxSizing: 'border-box',
};
